import { StyleProvider } from "@ant-design/cssinjs";
import { useTranslation } from "react-i18next";
import { RJSFSchema, UiSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import {
  anamnesisSchema,
  clientanamnese_clean_on_submit,
  clientanamnese_on_change_rule,
  clientanamnese_steps,
} from "./schema/anamneseSchema";
import { anamnesisUiSchema } from "./schema/anamneseUiSchema";
import { postsurvey_clean_on_submit, postsurvey_on_change_rule, postsurvey_steps, postsurveySchema } from "./schema/postsurveySchema";
import { postsurveyUiSchema } from "./schema/postsurveyUiSchema";
import { withTheme } from "@rjsf/core";
import { Theme as AntDTheme } from "@rjsf/antd";
import { Alert, Button, Steps, Spin } from "antd";
import React, { useContext, useEffect } from "react";
import { Paginator } from "./paginator";
import { HtmlDescriptionField, HtmlTitleField, MatrixField, SliderField, transformErrors } from "./rjsf.library";
import { makeApiRequest } from "./../../../lib/api";
import { GlobalStateContext } from "../../../lib/GlobalContext";
import "./style.css";

const Form = withTheme(AntDTheme);

interface EgwSurveyFormProps {
  params: any;
}

const EgwSurveyForm: React.FC<EgwSurveyFormProps> = ({ params }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>("");
  const [success, setSuccess] = React.useState<boolean>(false);
  const [paginator] = React.useState(() => new Paginator(setCurrentPage));
  const [schema, setSchema] = React.useState<RJSFSchema | null>(null);
  const [fullSchema, setFullSchema] = React.useState<RJSFSchema | null>({});
  const [fullUiSchema, setFullUiSchema] = React.useState<UiSchema | null>({});
  const [uiSchema, setUiSchema] = React.useState<UiSchema | null>(null);
  const [formSubmittedState, setFormSubmittedState] = React.useState(true);
  const [disabledNext, setDisabledNext] = React.useState(true);
  const formRef = React.useRef<any>(null);
  const { navigate } = useContext(GlobalStateContext);

  const localParamData = {
    survey: params?.s || ("UNKNOWN" as "CA" | "PS"),
    contact_id: parseInt(params?.cid || "41749"),
    info_id: parseInt(params?.iid || "161443"),
    intro: parseInt(params?.pi || "1"), // Product: intro
    original: parseInt(params?.po || "1"), // Product: original
    cpap: parseInt(params?.ct || "1"), // CpapTherapy
    osas_des: parseInt(params?.od || "1"), // OsasDisease
    submitSchemata: parseInt(params?.submitSchemata || "0"), // for sending blank schemata to Backend.
  };

  if (!["CA", "PS"].includes(localParamData.survey)) {
    // TODO redirect to error page
    throw new Error("404 Page not found");
  }
  const [dataStore, setDataStore] = React.useState<any>(localParamData);

  useEffect(() => {
    let currentUiSchema = null;
    switch (localParamData.survey) {
      case "CA":
        paginator.title = t("survey.anamnese.title");
        paginator.onChangeRule = clientanamnese_on_change_rule;
        paginator.onSubmit = clientanamnese_clean_on_submit;
        paginator.createSteps(clientanamnese_steps(t));
        currentUiSchema = anamnesisUiSchema(t);
        break;

      case "PS":
        paginator.title = t("survey.postsurvey.title");
        paginator.onChangeRule = postsurvey_on_change_rule;
        paginator.onSubmit = postsurvey_clean_on_submit;
        paginator.createSteps(postsurvey_steps(t));
        currentUiSchema = postsurveyUiSchema(t);
        break;
    }

    if (!currentUiSchema) {
      throw new Error("Invalid schema name");
    }

    setUiSchema(currentUiSchema);
    setFullUiSchema({ ...fullUiSchema, ...currentUiSchema });

    // CHECK IF SURVEY IS SUBMITTABE
    checkSubmitability();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    paginator.setCurrent(currentPage);

    if (localParamData.submitSchemata) {
      paginator.setCurrent(999);
    }
  }, [currentPage, paginator]);

  useEffect(() => {
    let currentSchema = null;
    switch (localParamData.survey) {
      case "PS":
        currentSchema = postsurveySchema(t, currentPage, localParamData);
        break;
      case "CA":
        currentSchema = anamnesisSchema(t, currentPage, localParamData);
        break;
    }
    if (!currentSchema) {
      throw new Error("Invalid schema name");
    }
    setSchema(currentSchema);
    setFullSchema({ ...fullSchema, ...currentSchema.properties });

    if (paginator.onChangeRule) {
      paginator.onChangeRule(dataStore, localParamData);
    }
    if (!paginator.getCurrentStep()?.notEmpty || Object.values(dataStore).length > 0) {
      setDisabledNext(false);
    } else {
      setDisabledNext(true);
    }
    setFormSubmittedState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginator, t]);

  useEffect(() => {
    // console.log("DEBUG: submitSchemata", currentPage, paginator.current, dataStore, fullSchema, schema);
    if (schema && currentPage === 999) {
      if (schema?.properties && "p_end" in schema.properties) {
        if (schema?.properties && ("p_start" in schema.properties || "start" in schema.properties)) {
          submit();
        }
      }
    }
  }, [schema]);

  useEffect(() => {
    // Execute the script logic when the component is mounted
    if (window.opener && localParamData.submitSchemata && success) {
      window.opener?.postMessage("survey-submitted", "*");
      window.close();
    }
  }, [localParamData.submitSchemata, success]); // Empty dependency array ensures this runs only once when the component mounts

  const checkSubmitability = async () => {
    // console.log("checkSubmittability");
    try {
      const request = {
        info_id: localParamData.info_id,
      };
      const response = await makeApiRequest(params, "REACT_SURVEY_CHECK_SUBMITABILITY", request);
      const parsedResponse = JSON.parse(response);
      if (parsedResponse!.MESSAGE !== "VALID") {
        setError(t("survey.custom.submit.submitability"));
        console.log("checkSubmitability", parsedResponse);
        return;
      }
    } catch (err) {
      setError(t("survey.custom.submit.error"));
    }
  };

  const submit = async () => {
    paginator.onSubmit(dataStore);
    // console.log("submit", dataStore);
    setError("");
    setLoading(true);
    try {
      const request = {
        data: dataStore,
        schema: fullSchema,
        ui_schema: fullUiSchema,
        info_id: localParamData.info_id,
      };
      const response = await makeApiRequest(params, "REACT_SURVEY_SUBMIT", request);
      const parsedResponse = JSON.parse(response);
      if (!parsedResponse || parsedResponse.STATUS !== "OK") {
        setError(t("survey.custom.submit.error"));
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(t("survey.custom.submit.error"));
    } finally {
      setLoading(false);
      setSuccess(true);
    }
  };

  const next = () => {
    if (validate()) {
      if (paginator.isEnd(currentPage)) {
        submit();
      } else {
        paginator.forward(dataStore);
      }
    }
  };

  const validate = () => (formRef.current as any).validateForm();
  const backward = () => paginator.backward(dataStore);
  const onChange = ({ formData }: any) => {
    const newDataStore = { ...dataStore, ...formData };
    paginator.onChangeRule(newDataStore, localParamData);
    setDataStore(newDataStore);
    setDisabledNext(false);
  };

  if (!schema || !uiSchema || !paginator || loading) {
    return <Spin />;
  }

  if (localParamData.submitSchemata && !success) {
    return (
      <div>
        <Spin />
      </div>
    );
  }

  if (localParamData.submitSchemata && success) {
    return (
      <div>
        <Alert message={t("survey.custom.submit.success_schema_submit")} type="success" showIcon />
      </div>
    );
  }

  
  if (error) {
    return (
      <div>
        <Alert message={error} type="error" showIcon />
        <br />
        {/* <Button
          type="primary"
          onClick={() => navigate(params, "home")}
        >
          {t("survey.custom.actions.redirect_to_clientarea")}
        </Button> */}
      </div>
    );
  }

  if (success) {
    return (
      <div>
        <Alert message={t("survey.custom.submit.success")} description={success} type="success" showIcon />
        {/* <br /> */}
        {/* <Button type="primary" onClick={() => navigate({ ...params, ...{ showMenu: true } }, "home")}>
          {t("survey.custom.actions.redirect_to_clientarea")}
        </Button> */}
      </div>
    );
  }



  return (
    <>    
      <h4>{paginator.title}</h4>
      <br /> <br />
      <Steps size="small" current={currentPage} items={paginator.antSteps} />
      <StyleProvider>
        <Form
          ref={formRef}
          formData={dataStore}
          schema={schema}
          liveValidate={formSubmittedState}
          uiSchema={uiSchema}
          validator={validator}
          onChange={onChange}
          transformErrors={(errors: any[]) => transformErrors(errors, schema, t)}
          onSubmit={next}
          onError={() => console.error("errors")}
          fields={{ HtmlDescriptionField, MatrixField, SliderField, HtmlTitleField }}
          formContext={{
            descriptionLocation: "tooltip",
            readonlyAsDisabled: false,
          }}
        >
          <div>
            {!paginator.isStart(currentPage) && (
              <>
                <Button onClick={backward}>{t("survey.actions.previous")}</Button>
                &nbsp;
              </>
            )}
            <Button type="primary" onClick={next} disabled={disabledNext}>
              {paginator.isEnd(currentPage) ? t("survey.actions.submit") : t("survey.actions.next")}
            </Button>
          </div>
        </Form>
      </StyleProvider>
    </>
  );
};

export default EgwSurveyForm;
